// services/thermalPrinterService.js

class ThermalPrinterService {
    constructor() {
        this.encoder = null;
        this.device = null;
        this.characteristic = null;
        this.endpointOut = null;
        this.interfaceNumber = null;
        this.isPrinting = false;
    }

    // Connexion via Web Bluetooth (imprimantes BT)
    async connectBluetooth() {
        try {
            this.device = await navigator.bluetooth.requestDevice({
                filters: [{ services: ['000018f0-0000-1000-8000-00805f9b34fb'] }],
                optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
            });

            const server = await this.device.gatt.connect();
            const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
            this.characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

            return true;
        } catch (error) {
            console.error('Erreur connexion Bluetooth:', error);
            throw error;
        }
    }

    // Connexion via Web USB
    async connectUSB() {
        try {
            this.device = await navigator.usb.requestDevice({
                filters: [
                    { vendorId: 0x0483 }, // STM
                    { vendorId: 0x0416 }, // Winbond
                    { vendorId: 0x04b8 }, // Epson
                    { vendorId: 0x0519 }, // Star
                    { vendorId: 0x1fc9 }, // NXP (utilisé par certaines imprimantes chinoises)
                    { vendorId: 0x0fe6 }, // ITE Tech (R290 et similaires)
                    { vendorId: 0x1a86 }, // QinHeng Electronics
                    { vendorId: 0x067b }, // Prolific (adaptateurs USB)
                    { vendorId: 0x0525 }, // PLX Technology
                    { vendorId: 0x28e9 }, // GD32 (certaines imprimantes chinoises)
                    { vendorId: 0x20d1 }, // Imprimantes thermiques génériques
                    { vendorId: 0x6868 }, // Imprimantes POS
                    { classCode: 7 }      // Classe imprimante USB
                ]
            });

            await this.device.open();

            // Sélectionner la configuration (généralement 1)
            if (this.device.configuration === null) {
                await this.device.selectConfiguration(1);
            }

            // Trouver l'interface et l'endpoint OUT
            const configuration = this.device.configuration;
            let foundEndpoint = false;

            for (const iface of configuration.interfaces) {
                for (const alternate of iface.alternates) {
                    // Chercher une interface de classe imprimante (7) ou vendor-specific (255)
                    if (alternate.interfaceClass === 7 || alternate.interfaceClass === 255 || alternate.interfaceClass === 0) {
                        for (const endpoint of alternate.endpoints) {
                            // Chercher un endpoint OUT (direction === 'out')
                            if (endpoint.direction === 'out') {
                                this.interfaceNumber = iface.interfaceNumber;
                                this.endpointOut = endpoint.endpointNumber;
                                foundEndpoint = true;
                                console.log(`Endpoint trouvé: Interface ${this.interfaceNumber}, Endpoint ${this.endpointOut}`);
                                break;
                            }
                        }
                    }
                    if (foundEndpoint) break;
                }
                if (foundEndpoint) break;
            }

            // Si aucun endpoint spécifique trouvé, chercher n'importe quel endpoint OUT
            if (!foundEndpoint) {
                for (const iface of configuration.interfaces) {
                    for (const alternate of iface.alternates) {
                        for (const endpoint of alternate.endpoints) {
                            if (endpoint.direction === 'out') {
                                this.interfaceNumber = iface.interfaceNumber;
                                this.endpointOut = endpoint.endpointNumber;
                                foundEndpoint = true;
                                console.log(`Endpoint OUT générique trouvé: Interface ${this.interfaceNumber}, Endpoint ${this.endpointOut}`);
                                break;
                            }
                        }
                        if (foundEndpoint) break;
                    }
                    if (foundEndpoint) break;
                }
            }

            if (!foundEndpoint) {
                throw new Error('Aucun endpoint OUT trouvé sur cette imprimante');
            }

            // Réclamer l'interface
            await this.device.claimInterface(this.interfaceNumber);

            // Sélectionner l'interface alternative si nécessaire
            const iface = configuration.interfaces[this.interfaceNumber];
            if (iface && iface.alternates.length > 0) {
                try {
                    await this.device.selectAlternateInterface(this.interfaceNumber, 0);
                } catch (e) {
                    // Ignorer l'erreur si l'interface alternative n'est pas supportée
                    console.log('selectAlternateInterface non nécessaire ou non supporté');
                }
            }

            console.log('Imprimante USB connectée avec succès');
            return true;
        } catch (error) {
            console.error('Erreur connexion USB:', error);
            throw error;
        }
    }

    // Génère les commandes ESC/POS
    generateTicketCommands(sale, shopInfo = {}) {
        
        console.log('generateTicketCommands', sale, shopInfo);
        const commands = [];

        // Initialisation
        commands.push([0x1B, 0x40]); // ESC @ - Reset
        commands.push([0x1B, 0x61, 0x01]); // Centrer

        // En-tête boutique
        commands.push([0x1D, 0x21, 0x10]); // Double hauteur/largeur
        commands.push(this.textToBytes(shopInfo.name || 'MA BOUTIQUE'));
        commands.push([0x0A]);
        commands.push([0x1D, 0x21, 0x00]); // Taille normale

        if (shopInfo.address) {
            commands.push(this.textToBytes(shopInfo.address));
            commands.push([0x0A]);
        }
        if (shopInfo.phone) {
            commands.push(this.textToBytes(`Tel: ${shopInfo.phone}`));
            commands.push([0x0A]);
        }

        // Ligne séparatrice
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);

        // Infos ticket
        commands.push([0x1B, 0x61, 0x00]); // Aligner gauche
        commands.push(this.textToBytes(`Ticket: ${sale.invoice_number}`));
        commands.push([0x0A]);
        commands.push(this.textToBytes(`Date: ${this.formatDate(sale.sale_date)}`));
        commands.push([0x0A]);

        if (sale.user?.name) {
            commands.push(this.textToBytes(`Vendeur: ${sale.user.name}`));
            commands.push([0x0A]);
        }

        if (sale.customer?.name) {
            commands.push(this.textToBytes(`Client: ${sale.customer.name} - ${sale.customer.phone}`));
            commands.push([0x0A]);
        }

        // Ligne séparatrice
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);

        // Articles
        if (sale.sale_items && sale.sale_items.length > 0) {
            for (const item of sale.sale_items) {
                console.log(item)
                const name = this.truncate(item.product?.name || item.product_name, 30);
                const qty = item.quantity;
                const price = this.formatAmount(item.unit_price);
                const total = this.formatAmount(item.total_amount);

                commands.push(this.textToBytes(name));
                commands.push([0x0A]);
                commands.push(this.textToBytes(`  ${qty} x ${price} = ${total}`));
                commands.push([0x0A]);
            }
        }

        // Ligne séparatrice
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);

        // Totaux
        commands.push([0x1B, 0x61, 0x02]); // Aligner droite

        if (parseFloat(sale.discount) > 0) {
            commands.push(this.textToBytes(`Remise: -${this.formatAmount(sale.discount)}`));
            commands.push([0x0A]);
        }

        // Total en gras
        commands.push([0x1B, 0x45, 0x01]); // Gras ON
        commands.push([0x1D, 0x21, 0x01]); // Double hauteur
        commands.push(this.textToBytes(`TOTAL: ${this.formatAmount(sale.total_incl_tax)}`));
        commands.push([0x0A]);
        commands.push([0x1D, 0x21, 0x00]); // Taille normale
        commands.push([0x1B, 0x45, 0x00]); // Gras OFF

        // Mode de paiement
        if (sale.payment) {
            commands.push([0x1B, 0x61, 0x00]); // Aligner gauche
            commands.push(this.textToBytes(`Paiement: ${this.getPaymentMethod(sale.payment.payment_type)}`));
            commands.push([0x0A]);
            commands.push(this.textToBytes(`Montant recu: ${this.formatAmount(sale.payment.amount)}`));
            commands.push([0x0A]);
        }

        // Pied de page
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);
        commands.push([0x1B, 0x61, 0x01]); // Centrer
        commands.push(this.textToBytes('Merci de votre visite !'));
        commands.push([0x0A]);
        commands.push([0x0A]);

        // Coupe papier
        commands.push([0x1D, 0x56, 0x41, 0x03]); // GS V A - Coupe partielle

        return commands;
    }

    // Impression via Bluetooth
    async printBluetooth(commands) {
        const data = this.flattenCommands(commands);
        const chunkSize = 20;

        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            await this.characteristic.writeValue(new Uint8Array(chunk));
            await this.delay(50);
        }
    }

    // Impression via USB
    async printUSB(commands) {
        if (!this.endpointOut) {
            throw new Error('Endpoint USB non configuré. Veuillez reconnecter l\'imprimante.');
        }

        const data = new Uint8Array(this.flattenCommands(commands));

        // Envoyer les données par chunks pour éviter les problèmes de buffer
        const chunkSize = 64; // Taille standard pour la plupart des imprimantes USB

        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, Math.min(i + chunkSize, data.length));
            await this.device.transferOut(this.endpointOut, chunk);
            // Petit délai entre les chunks pour laisser l'imprimante traiter
            await this.delay(10);
        }

        // Flush après impression - envoyer commandes de fin
        await this.flushPrinter();
    }

    // Flush/Nettoyage de l'imprimante après impression
    async flushPrinter() {
        if (!this.device || !this.endpointOut) return;

        try {
            // Commandes de flush ESC/POS
            const flushCommands = new Uint8Array([
                0x0A, 0x0A, 0x0A,  // 3 sauts de ligne pour s'assurer que tout est sorti
                0x1B, 0x40         // ESC @ - Reset/Init imprimante (nettoie le buffer)
            ]);

            await this.device.transferOut(this.endpointOut, flushCommands);
            await this.delay(100); // Attendre que l'imprimante traite le flush
        } catch (error) {
            console.warn('Erreur flush imprimante:', error);
        }
    }

    // Impression via serveur (réseau/série)
    async printViaServer(sale, printerConfig = {}) {
        const response = await fetch('/api/print/ticket', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
                sale_id: sale.id,
                printer_ip: printerConfig.ip,
                printer_port: printerConfig.port || 9100,
                printer_type: printerConfig.type || 'network'
            })
        });

        if (!response.ok) {
            throw new Error('Erreur impression serveur');
        }

        return response.json();
    }

    // Méthode principale d'impression
    async printTicket(sale, options = {}) {
        const {
            connectionType = 'server', // 'bluetooth', 'usb', 'server'
            shopInfo = {},
            printerConfig = {}
        } = options;

        // Empêcher les impressions simultanées
        if (this.isPrinting) {
            throw new Error('Une impression est déjà en cours. Veuillez patienter.');
        }

        this.isPrinting = true;

        try {
            switch (connectionType) {
                case 'bluetooth': {
                    if (!this.characteristic) {
                        await this.connectBluetooth();
                    }
                    const btCommands = this.generateTicketCommands(sale, shopInfo);
                    await this.printBluetooth(btCommands);
                    break;
                }

                case 'usb': {
                    // Vérifier si l'appareil est connecté et ouvert
                    if (!this.device || !this.device.opened || !this.endpointOut) {
                        // Déconnecter proprement si nécessaire
                        if (this.device) {
                            await this.disconnect();
                        }
                        await this.connectUSB();
                    }
                    const usbCommands = this.generateTicketCommands(sale, shopInfo);
                    await this.printUSB(usbCommands);
                    break;
                }

                case 'server':
                default:
                    await this.printViaServer(sale, printerConfig);
                    break;
            }

            // Délai de sécurité après impression pour laisser l'imprimante terminer
            await this.delay(200);

            return { success: true, message: 'Ticket imprimé avec succès' };
        } catch (error) {
            console.error('Erreur impression:', error);
            // En cas d'erreur, reset la connexion pour la prochaine tentative
            if (connectionType === 'usb') {
                await this.disconnect();
            }
            throw error;
        } finally {
            this.isPrinting = false;
        }
    }

    // Utilitaires
    textToBytes(text) {
        // Nettoyer le texte avant conversion
        const cleanText = this.sanitizeText(text);
        const encoder = new TextEncoder();
        return Array.from(encoder.encode(cleanText));
    }

    // Nettoyer le texte : supprimer accents et caractères spéciaux
    sanitizeText(text) {
        if (!text) return '';

        // Utiliser normalize pour décomposer les caractères accentués
        // puis supprimer les diacritiques (accents)
        let result = text
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, ''); // Supprimer les diacritiques

        // Remplacer les caractères spéciaux courants
        const specialChars = {
            '\u0153': 'oe',  // œ
            '\u0152': 'OE',  // Œ
            '\u00e6': 'ae',  // æ
            '\u00c6': 'AE',  // Æ
            '\u00df': 'ss',  // ß
            '\u20ac': 'EUR', // €
            '\u00a3': 'GBP', // £
            '\u00a5': 'YEN', // ¥
            '\u00a9': '(c)', // ©
            '\u00ae': '(R)', // ®
            '\u2122': 'TM',  // ™
            '\u00b0': 'o',   // °
            '\u00b2': '2',   // ²
            '\u00b3': '3',   // ³
            '\u00ab': '"',   // «
            '\u00bb': '"',   // »
            '\u2018': "'",   // '
            '\u2019': "'",   // '
            '\u201c': '"',   // "
            '\u201d': '"',   // "
            '\u2013': '-',   // –
            '\u2014': '-',   // —
            '\u2026': '...', // …
            '\u2022': '*'    // •
        };

        for (const [char, replacement] of Object.entries(specialChars)) {
            result = result.split(char).join(replacement);
        }

        // Supprimer tous les caractères non-ASCII restants (garder uniquement ASCII imprimable + espace)
        result = result.replace(/[^\x20-\x7E\n\r\t]/g, ' ');

        return result;
    }

    flattenCommands(commands) {
        return commands.flat();
    }

    truncate(str, length) {
        if (str.length <= length) return str;
        return str.substring(0, length - 3) + '...';
    }

    formatAmount(amount) {
        const num = Math.round(amount || 0);
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' F';
    }

    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getPaymentMethod(type) {
        const methods = {
            'cash': 'Espèces',
            'card': 'Carte',
            'mobile_money': 'Mobile Money',
            'orange_money': 'Orange Money',
            'wave': 'Wave',
            'check': 'Chèque',
            'bank_transfer': 'Virement',
            'other': 'Autre'
        };
        return methods[type] || type;
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Déconnexion
    async disconnect() {
        try {
            // Déconnexion Bluetooth
            if (this.device?.gatt?.connected) {
                this.device.gatt.disconnect();
            }

            // Déconnexion USB
            if (this.device?.opened) {
                if (this.interfaceNumber !== null) {
                    try {
                        await this.device.releaseInterface(this.interfaceNumber);
                    } catch (e) {
                        console.log('Erreur release interface:', e);
                    }
                }
                await this.device.close();
            }
        } catch (error) {
            console.error('Erreur déconnexion:', error);
        }

        this.device = null;
        this.characteristic = null;
        this.endpointOut = null;
        this.interfaceNumber = null;
        this.isPrinting = false;
    }

    // Vérifier si l'imprimante est connectée
    isConnected() {
        return this.device !== null && (this.characteristic !== null || this.endpointOut !== null);
    }

    // Reconnecter l'imprimante USB si nécessaire
    async ensureUSBConnection() {
        if (!this.device || !this.device.opened) {
            await this.connectUSB();
        }
    }
}

export default new ThermalPrinterService();